import { ChartSeriesSwatch } from './ChartSeriesSwatch';
import { pmcSeriesLabel } from '../../utils/pmcSeriesLabel';
import { displayDigits } from '../../utils/displayDigits';
/**
 * Interactive PMC / periodic chart — hover cursor, zoom/pan, multi-series.
 * Web port of Ai-Tempo PeriodicChart.tsx.
 */
import React, { useCallback, useId, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChartScrub } from '../../hooks/useChartScrub';
import { usePinchZoom } from '../../hooks/usePinchZoom';
import { formatShortDate, formatIsoDateLabel } from '../../i18n/formatters';
import {
  buildPeriodicLines,
  catmullRom,
  CHART_H,
  CHART_W,
  PAD_B,
  PAD_L,
  PAD_R,
  PAD_T,
  VIEWBOX_HEIGHT,
  VIEWBOX_WIDTH,
  type LineSpec,
} from './periodicChartBuilder';
import { mergePmcSettings, type PmcChartSettings } from './pmcSettings';
import { EMBER, GREEN } from './palette';
import { TSB_REFERENCE_BANDS } from './chartReferences';

import { pmcDisplayPoints } from './pmcDisplayPoints';
import { PmcPeakTrendPanel } from './PmcPeakTrendPanel';

const GRID_COLOR = 'var(--chart-grid, rgba(163,177,198,0.28))';
const LABEL_COLOR = 'var(--chart-label, #9AA1B1)';
const CURSOR_COLOR = 'var(--chart-label, #9AA1B1)';

type PmcLegendSeriesKey = 'ctl' | 'atl' | 'tsb' | 'tss';

export interface ChartPeriodicProps {
  data: unknown;
  settings?: Record<string, unknown>;
  style?: React.CSSProperties;
  /** Stretch chart to fill parent height (used in fullscreen overlay). */
  fullscreen?: boolean;
  dateRange?: { start: string; end: string };
  /** Opens workout detail when a peak dot with workout_id is clicked. */
  onWorkoutClick?: (workoutId: number) => void;
}

function formatDateLabel(timestamp: number, locale?: string, showYear = false): string {
  const d = new Date(timestamp);
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return showYear ? formatIsoDateLabel(iso, locale) : formatShortDate(iso, locale);
}

/** Screen clientX → viewBox X (handles SVG letterboxing via getScreenCTM). */
function clientXToViewBoxX(svg: SVGSVGElement | null, clientX: number): number | null {
  if (!svg) return null;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = 0;
  return pt.matrixTransform(ctm.inverse()).x;
}

export function ChartPeriodic({ data, settings: rawSettings, style, dateRange, fullscreen = false, onWorkoutClick }: ChartPeriodicProps) {
  const { t, i18n } = useTranslation();
  const plotClip = useId().replace(/:/g, '');
  const isRtl = i18n.dir() === 'rtl';
  const settings = useMemo(() => mergePmcSettings(rawSettings), [rawSettings]);
  const rangeStart = dateRange ? new Date(dateRange.start).getTime() : NaN;
  const rangeEnd = dateRange ? new Date(dateRange.end).getTime() : NaN;
  const hasDateRange = Number.isFinite(rangeStart) && Number.isFinite(rangeEnd) && rangeEnd >= rangeStart;
  const allLines = useMemo(() => buildPeriodicLines(data, settings).map(line => hasDateRange
    ? { ...line, points: line.points.filter(point => point.x >= rangeStart && point.x <= rangeEnd) }
    : line), [data, settings, hasDateRange, rangeStart, rangeEnd]);
  const [hiddenSeries, setHiddenSeries] = useState<Set<PmcLegendSeriesKey>>(() => new Set(['tss']));
  const previousSeries = useRef<Set<PmcLegendSeriesKey>>(new Set(['tss']));
  const lines = useMemo(
    () => allLines.filter((line) => {
      if (line.peakMetric) return false;
      if (line.label.includes('CTL')) return !hiddenSeries.has('ctl');
      if (line.label.includes('ATL')) return !hiddenSeries.has('atl');
      if (line.label.includes('TSB')) return !hiddenSeries.has('tsb');
      if (line.kind === 'bar' && line.label === 'TSS') return !hiddenSeries.has('tss');
      return true;
    }),
    [allLines, hiddenSeries],
  );

  const peakLines = useMemo(() => allLines.filter(line => line.peakMetric), [allLines]);
  const peaksEnabled = !!(settings.best_peaks_1?.show_best_peaks || settings.best_peaks_2?.show_best_peaks);

  const toggleSeries = useCallback((key: PmcLegendSeriesKey) => {
    setHiddenSeries((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const pmcLegendItems = useMemo(() => {
    const ctl = allLines.find((line) => line.label.includes('CTL'));
    const atl = allLines.find((line) => line.label.includes('ATL'));
    const tsb = allLines.find((line) => line.label.includes('TSB'));
    const tss = allLines.find((line) => line.kind === 'bar' && line.label === 'TSS');
    return [
      ctl ? { key: 'ctl' as const, label: pmcSeriesLabel('Fitness · CTL', t), color: ctl.color, dash: ctl.dashArray, circle: false } : null,
      atl ? { key: 'atl' as const, label: pmcSeriesLabel('Fatigue · ATL', t), color: atl.color, dash: atl.dashArray, circle: false } : null,
      tsb ? { key: 'tsb' as const, label: pmcSeriesLabel('Form · TSB', t), color: tsb.color, dash: '6 4', circle: false } : null,
      tss ? { key: 'tss' as const, label: pmcSeriesLabel('TSS', t), color: tss.color, dash: undefined, circle: true } : null,
    ].filter((item): item is NonNullable<typeof item> => item != null);
  }, [allLines, t]);

  const [viewState, setViewState] = useState({ zoom: 1, pan: 0 });
  const viewStateRef = useRef(viewState);
  viewStateRef.current = viewState;

  const [focusedT, setFocusedT] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [plotHeight, setPlotHeight] = useState(360);
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const measure = () => setPlotHeight(svg.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(svg);
    return () => observer.disconnect();
  }, [allLines.length, fullscreen]);
  const panRef = useRef<{ startVBX: number; startPan: number } | null>(null);

  useEffect(() => {
    setViewState({ zoom: 1, pan: 0 });
    setFocusedT(null);
    panRef.current = null;
  }, [rangeStart, rangeEnd]);

  const { minT, maxT } = useMemo(() => {
    if (hasDateRange) return { minT: rangeStart, maxT: rangeEnd };
    const all = allLines.flatMap((l) => l.points.map((p) => p.x));
    if (!all.length) return { minT: 0, maxT: 1 };
    return { minT: Math.min(...all), maxT: Math.max(...all) };
  }, [allLines, hasDateRange, rangeStart, rangeEnd]);

  const tRange = Math.max(1, maxT - minT);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !fullscreen) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const xVB = clientXToViewBoxX(svgRef.current, e.clientX);
      if (xVB == null) return;
      const physicalRatio = Math.max(0, Math.min(1, (xVB - PAD_L) / CHART_W));
      const centerRatio = physicalRatio;
      const { zoom: initZoom, pan: initPan } = viewStateRef.current;
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      const newZoom = Math.max(1, Math.min(20, initZoom * factor));
      const initVisDur = tRange / initZoom;
      const initMaxStart = Math.max(0, tRange - initVisDur);
      const anchorT = minT + initPan * initMaxStart + centerRatio * initVisDur;
      const newVisDur = tRange / newZoom;
      const newMaxStart = Math.max(0, tRange - newVisDur);
      const newPan =
        newMaxStart > 0
          ? Math.max(0, Math.min(1, (anchorT - centerRatio * newVisDur - minT) / newMaxStart))
          : 0;
      setViewState({ zoom: newZoom, pan: newPan });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [tRange, minT, fullscreen]);

  const visibleRange = useMemo(() => {
    const { zoom, pan } = viewState;
    const visDuration = tRange / zoom;
    const maxStart = Math.max(0, tRange - visDuration);
    const start = minT + pan * maxStart;
    return { start, end: start + visDuration };
  }, [viewState, tRange, minT]);

  const ctlOnly = lines.length > 0 && lines.every(line => line.label.includes('CTL'));
  const leftRange = useMemo(() => {
    const vals = lines.filter((l) => l.axis === 'left').flatMap((l) => l.points.map((p) => p.y));
    if (!vals.length) return { min: 0, max: 1 };
    if (ctlOnly) {
      const visible = lines.flatMap(line => line.points.filter(point => point.x >= visibleRange.start && point.x <= visibleRange.end).map(point => point.y)).filter(Number.isFinite);
      const values = visible.length ? visible : vals;
      const min = Math.min(...values), max = Math.max(...values);
      const padding = Math.max(2, (max - min) * .15);
      return { min: Math.max(0, min - padding), max: max + padding };
    }
    return { min: Math.min(...vals, 0), max: Math.max(...vals, 0) };
  }, [lines, ctlOnly, visibleRange]);

  const hasIfAxis = lines.some(line => line.axis === 'if');
  const ifMax = Math.ceil(Math.max(1.2, ...lines.filter(line => line.axis === 'if').flatMap(line => line.points.map(point => point.y)).filter(Number.isFinite)) * 5) / 5;
  const toIfY = (value: number) => PAD_T + CHART_H - value / ifMax * CHART_H;

  const rightMax = useMemo(() => {
    const vals = lines.filter((l) => l.axis === 'right').flatMap((l) => l.points.map((p) => p.y));
    if (!vals.length) return 1;
    return Math.max(...vals, 1);
  }, [lines]);

  const toX = useCallback(
    (t: number) => {
      const range = Math.max(1, visibleRange.end - visibleRange.start);
      const ratio = (t - visibleRange.start) / range;
      return PAD_L + ratio * CHART_W;
    },
    [visibleRange],
  );

  const toLeftY = useCallback(
    (v: number) => {
      const range = Math.max(1, leftRange.max - leftRange.min);
      return PAD_T + CHART_H - ((v - leftRange.min) / range) * CHART_H;
    },
    [leftRange],
  );

  const toRightY = useCallback(
    (v: number) => PAD_T + CHART_H - (v / Math.max(1, rightMax)) * CHART_H,
    [rightMax],
  );

  const allTimestamps = useMemo(() => {
    const set = new Set<number>();
    allLines.forEach((l) => l.points.forEach((p) => set.add(p.x)));
    return Array.from(set).sort((a, b) => a - b);
  }, [allLines]);

  /** Map mouse X → nearest data timestamp by pixel distance (matches visible cursor line). */
  const locationToT = useCallback(
    (clientX: number): number | null => {
      if (!allTimestamps.length) return null;
      const xVB = clientXToViewBoxX(svgRef.current, clientX);
      if (xVB == null || xVB < PAD_L || xVB > PAD_L + CHART_W) return null;

      let nearest = allTimestamps[0];
      let bestDist = Math.abs(toX(nearest) - xVB);
      for (let i = 1; i < allTimestamps.length; i++) {
        const dist = Math.abs(toX(allTimestamps[i]) - xVB);
        if (dist < bestDist) {
          bestDist = dist;
          nearest = allTimestamps[i];
        }
      }
      return nearest;
    },
    [allTimestamps, toX],
  );

  const panActiveRef = useRef(false);

  const applyPinchZoom = useCallback(
    (factor: number, centerClientX: number) => {
      const xVB = clientXToViewBoxX(svgRef.current, centerClientX);
      if (xVB == null) return;
      const physicalRatio = Math.max(0, Math.min(1, (xVB - PAD_L) / CHART_W));
      const centerRatio = physicalRatio;
      const { zoom: initZoom, pan: initPan } = viewStateRef.current;
      const newZoom = Math.max(1, Math.min(20, initZoom * factor));
      const initVisDur = tRange / initZoom;
      const initMaxStart = Math.max(0, tRange - initVisDur);
      const anchorT = minT + initPan * initMaxStart + centerRatio * initVisDur;
      const newVisDur = tRange / newZoom;
      const newMaxStart = Math.max(0, tRange - newVisDur);
      const newPan =
        newMaxStart > 0
          ? Math.max(0, Math.min(1, (anchorT - centerRatio * newVisDur - minT) / newMaxStart))
          : 0;
      setViewState({ zoom: newZoom, pan: newPan });
    },
    [tRange, minT],
  );

  usePinchZoom(wrapRef, applyPinchZoom, fullscreen && lines.length > 0);

  const handlePointerAtX = useCallback(
    (clientX: number) => {
      const t = locationToT(clientX);
      if (t != null) setFocusedT(t);
      if (panActiveRef.current && viewStateRef.current.zoom > 1) {
        const currentVBX = clientXToViewBoxX(svgRef.current, clientX);
        if (currentVBX == null || !panRef.current) return;
        const dxVB = currentVBX - panRef.current.startVBX;
        const { zoom } = viewStateRef.current;
        const visDur = tRange / zoom;
        const maxStart = Math.max(0, tRange - visDur);
        if (maxStart > 0) {
          const direction = -1;
          const dPan = (direction * dxVB / CHART_W) * (visDur / maxStart);
          setViewState((vs) => ({
            ...vs,
            pan: Math.max(0, Math.min(1, panRef.current!.startPan + dPan)),
          }));
        }
      }
    },
    [locationToT, tRange],
  );

  const listScrub = useChartScrub(lines.length > 0, handlePointerAtX, () => setFocusedT(null), false);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (viewStateRef.current.zoom > 1) {
        const startVBX = clientXToViewBoxX(svgRef.current, e.clientX);
        if (startVBX != null) {
          panRef.current = { startVBX, startPan: viewStateRef.current.pan };
          panActiveRef.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
        }
      } else {
        handlePointerAtX(e.clientX);
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    },
    [handlePointerAtX],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse' || panActiveRef.current || e.buttons > 0) {
        handlePointerAtX(e.clientX);
      }
    },
    [handlePointerAtX],
  );

  const onPointerUp = useCallback(() => {
    panActiveRef.current = false;
    panRef.current = null;
  }, []);

  const onPointerLeave = useCallback(() => {
    if (!panActiveRef.current) {
      setFocusedT(null);
    }
  }, []);

  const handleWheel = useCallback((_e: React.WheelEvent) => {
    /* handled via native listener (passive: false) */
  }, []);

  const leftAxisLabels = useMemo(() => {
    const steps = 4;
    return Array.from({ length: steps }, (_, i) => {
      const v = leftRange.max - (i / (steps - 1)) * (leftRange.max - leftRange.min);
      return Math.round(v).toString();
    });
  }, [leftRange]);

  const rightAxisLabels = useMemo(() => {
    if (!lines.some((l) => l.axis === 'right')) return [];
    const steps = 4;
    return Array.from({ length: steps }, (_, i) => {
      const v = rightMax - (i / (steps - 1)) * rightMax;
      return Math.round(v).toString();
    });
  }, [lines, rightMax]);

  const xAxisTicks = useMemo(() => {
    const { start, end } = visibleRange;
    if (end <= start) return [];
    const range = end - start;
    const showYear = range > 180 * 24 * 60 * 60 * 1000;
    const count = showYear ? 3 : 5;
    return Array.from({ length: count }, (_, i) => {
      const t = start + (i / (count - 1)) * range;
      return { t, label: formatDateLabel(t, i18n.language, showYear) };
    });
  }, [visibleRange, i18n.language]);

  const barWidth = useMemo(() => {
    const barXs = lines
      .filter((l) => l.kind === 'bar')
      .flatMap((l) => l.points.map((p) => p.x))
      .sort((a, b) => a - b);
    if (barXs.length < 2) return Math.max(2, CHART_W / 60);
    let minGap = Infinity;
    for (let i = 1; i < barXs.length; i++) {
      const gap = barXs[i] - barXs[i - 1];
      if (gap > 0 && gap < minGap) minGap = gap;
    }
    if (!Number.isFinite(minGap)) return Math.max(2, CHART_W / 60);
    const visRange = visibleRange.end - visibleRange.start;
    const w = (minGap / Math.max(1, visRange)) * CHART_W * 0.7;
    return Math.max(2, Math.min(w, CHART_W / 20));
  }, [lines, visibleRange]);

  const legendItems = useMemo(() => {
    return lines.map((l) => {
      let value: number | null = null;
      if (focusedT != null && l.points.length) {
        let nearest = l.points[0];
        let bestDist = Math.abs(focusedT - nearest.x);
        for (let i = 1; i < l.points.length; i++) {
          const dist = Math.abs(focusedT - l.points[i].x);
          if (dist < bestDist) {
            bestDist = dist;
            nearest = l.points[i];
          }
        }
        const oneAndHalfDay = 36 * 3600 * 1000;
        if (bestDist <= oneAndHalfDay) value = nearest.y;
      }
      return { label: l.label, color: l.color, value };
    });
  }, [lines, focusedT]);

  const focusedX = focusedT != null ? toX(focusedT) : null;
  const showTsbFill = settings.pmc?.show_form_TSB_fill !== false && settings.pmc?.show_form_TSB !== false;
  const showPositiveTsbFill = showTsbFill && !hiddenSeries.has('tsb');
  const showNegativeTsbFill = showTsbFill && !hiddenSeries.has('tsb');

  const displayPoints = (line: LineSpec) => pmcDisplayPoints(line.points.filter(point => Number.isFinite(point.y)), (visibleRange.end - visibleRange.start) / 86400000);
  const renderLine = (l: LineSpec) => {
    const pts = displayPoints(l);
    if (pts.length < 2) return null;
    const coords: [number, number][] = pts.map((p) => [toX(p.x), l.axis === 'if' ? toIfY(p.y) : l.axis === 'right' ? toRightY(p.y) : toLeftY(p.y)]);
    return (
      <path
        key={`line-${l.label}`}
        d={catmullRom(coords)}
        fill="none"
        stroke={l.color}
        strokeWidth={l.label.includes('CTL') ? 1.8 : l.dashed ? 1.3 : 1.6}
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={l.dashArray ?? (l.dashed ? '6 4' : undefined)}
      />
    );
  };

  const renderDotline = (l: LineSpec) => {
    const pts = l.points.filter((p) => Number.isFinite(p.y));
    if (!pts.length) return null;
    const coords: [number, number][] = pts.map((p) => [toX(p.x), l.axis === 'if' ? toIfY(p.y) : l.axis === 'right' ? toRightY(p.y) : toLeftY(p.y)]);
    return (
      <g key={`dotline-${l.label}`}>
        {coords.length > 1 && (
          <path
            d={catmullRom(coords)}
            fill="none"
            stroke={l.color}
            strokeDasharray={l.dashArray}
            strokeWidth={1}
            opacity={0.6}
            strokeLinecap="round"
          />
        )}
        {pts.map((p, i) => {
          const x = toX(p.x);
          const y = l.axis === 'if' ? toIfY(p.y) : l.axis === 'right' ? toRightY(p.y) : toLeftY(p.y);
          const clickable = p.workoutId != null && p.workoutId > 0;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={clickable ? 5 : 3}
              fill={l.color}
              stroke="#FFFFFF"
              strokeWidth={1}
              style={{ cursor: clickable ? 'pointer' : undefined }}
              onPointerDown={(e) => { if (clickable) e.stopPropagation(); }}
              onClick={(e) => {
                if (!clickable || !p.workoutId) return;
                e.stopPropagation();
                onWorkoutClick?.(p.workoutId);
              }}
            />
          );
        })}
      </g>
    );
  };

  const renderBars = (l: LineSpec) => (
    <g key={`bars-${l.label}`}>
      {l.points.map((p, pi) => {
        const x = toX(p.x) - barWidth / 2;
        const y = toRightY(p.y);
        const h = PAD_T + CHART_H - y;
        if (h <= 0) return null;
        return (
          <rect
            key={pi}
            x={x}
            y={y}
            width={barWidth}
            height={h}
            rx={2}
            fill={l.color}
            opacity={0.55}
          />
        );
      })}
    </g>
  );

  if (!allLines.length) {
    return (
      <div style={{ padding: '24px 0', color: 'var(--fg-4)', fontSize: 12, textAlign: 'center', ...style }}>
        No chart data for the selected range
      </div>
    );
  }

  const tsbLine = lines.find((l) => l.label.includes('TSB'));
  const tsbPts = tsbLine ? displayPoints(tsbLine) : [];
  // Keep short HTML labels close to their bands, with enough room at any chart height.
  const bandLabels = tsbPts.length ? TSB_REFERENCE_BANDS.flatMap(zone => {
    const low = Math.max(leftRange.min, zone.min);
    const high = Math.min(leftRange.max, zone.max);
    if (high <= low) return [];
    return [{ zone, y: toLeftY((low + high) / 2) / VIEWBOX_HEIGHT * plotHeight }];
  }).sort((a, b) => a.y - b.y) : [];
  const labelTop = PAD_T / VIEWBOX_HEIGHT * plotHeight + 10;
  const labelBottom = (PAD_T + CHART_H) / VIEWBOX_HEIGHT * plotHeight - 10;
  bandLabels.forEach((label, i) => {
    label.y = Math.max(label.y, i ? bandLabels[i - 1].y + 22 : labelTop);
  });
  for (let i = bandLabels.length - 1; i >= 0; i--) {
    bandLabels[i].y = Math.min(bandLabels[i].y, i === bandLabels.length - 1 ? labelBottom : bandLabels[i + 1].y - 22);
  }

  const rootStyle: React.CSSProperties = fullscreen
    ? { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, ...style }
    : { ...style };

  const chartAreaStyle: React.CSSProperties = fullscreen
    ? { position: 'relative', userSelect: 'none', touchAction: 'none', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
    : { position: 'relative', userSelect: 'none', touchAction: 'pan-y pinch-zoom' };

  const svgStyle: React.CSSProperties = fullscreen
    ? { width: '100%', height: '100%', flex: 1, minHeight: 0, display: 'block' }
    : { width: '100%', height: 'auto', minHeight: 320, aspectRatio: `${VIEWBOX_WIDTH} / 380`, display: 'block' };

  return (
    <div style={rootStyle}>
      <div
        ref={wrapRef}
        className={`chart-touch-plot${fullscreen ? ' pinch-zoom-zone' : ''}`}
        style={{ ...chartAreaStyle, marginRight: hasIfAxis ? 38 : undefined }}
        onPointerDown={fullscreen ? onPointerDown : listScrub.onPointerDown}
        onPointerMove={fullscreen ? onPointerMove : listScrub.onPointerMove}
        onPointerUp={fullscreen ? onPointerUp : listScrub.onPointerUp}
        onPointerCancel={fullscreen ? onPointerUp : listScrub.onPointerCancel}
        onPointerLeave={fullscreen ? onPointerLeave : listScrub.onPointerLeave}
        onWheel={handleWheel}
        onDoubleClick={() => setViewState({ zoom: 1, pan: 0 })}
      >
      <div style={{ position: 'relative', flex: 1, minHeight: fullscreen ? 220 : undefined }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        preserveAspectRatio="none"
        style={svgStyle}
      >
        <defs>
          <clipPath id={plotClip}><rect x={PAD_L} y={PAD_T - 2} width={CHART_W} height={CHART_H + 4} /></clipPath>
          {lines.map((l, si) => (
            l.color2 ? (
              <linearGradient key={`bg${si}`} id={`bg${si}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={l.color} stopOpacity="0.95" />
                <stop offset="100%" stopColor={l.color2} stopOpacity="0.5" />
              </linearGradient>
            ) : null
          ))}
        </defs>
        <g clipPath={`url(#${plotClip})`}>

        {tsbPts.length > 0 && TSB_REFERENCE_BANDS.map(zone => {
          const low = Math.max(leftRange.min, zone.min);
          const high = Math.min(leftRange.max, zone.max);
          if (high <= low) return null;
          return <rect key={zone.key} x={PAD_L} y={toLeftY(high)} width={CHART_W}
            height={toLeftY(low) - toLeftY(high)} fill={zone.color} opacity={0.07} pointerEvents="none" />;
        })}

        {leftAxisLabels.map((_, i) => {
          const y = PAD_T + (CHART_H / (leftAxisLabels.length - 1)) * i;
          return (
            <g key={`gl${i}`}>
              <line x1={PAD_L} y1={y} x2={VIEWBOX_WIDTH - PAD_R} y2={y} stroke={GRID_COLOR} strokeWidth={1} strokeDasharray="3 5" />
            </g>
          );
        })}

        {leftRange.min < 0 && leftRange.max > 0 && (
          <line
            x1={PAD_L}
            y1={toLeftY(0)}
            x2={VIEWBOX_WIDTH - PAD_R}
            y2={toLeftY(0)}
            stroke={LABEL_COLOR}
            strokeWidth={1}
            opacity={0.5}
          />
        )}

        {lines.filter((l) => l.kind === 'bar').map(renderBars)}

        {(showPositiveTsbFill || showNegativeTsbFill) && tsbPts.length > 1 && (() => {
          const zy = toLeftY(0);
          let dPos = `M${toX(tsbPts[0].x).toFixed(1)},${zy.toFixed(1)}`;
          tsbPts.forEach((p) => { dPos += ` L${toX(p.x).toFixed(1)},${toLeftY(Math.max(0, p.y)).toFixed(1)}`; });
          dPos += ` L${toX(tsbPts[tsbPts.length - 1].x).toFixed(1)},${zy.toFixed(1)} Z`;
          let dNeg = `M${toX(tsbPts[0].x).toFixed(1)},${zy.toFixed(1)}`;
          tsbPts.forEach((p) => { dNeg += ` L${toX(p.x).toFixed(1)},${toLeftY(Math.min(0, p.y)).toFixed(1)}`; });
          dNeg += ` L${toX(tsbPts[tsbPts.length - 1].x).toFixed(1)},${zy.toFixed(1)} Z`;
          return (
            <>
              {showPositiveTsbFill && <path d={dPos} fill={GREEN} fillOpacity={0.18} />}
              {showNegativeTsbFill && <path d={dNeg} fill={EMBER} fillOpacity={0.14} />}
            </>
          );
        })()}

        {lines.filter((l) => l.kind === 'line').map(renderLine)}
        {lines.filter((l) => l.kind === 'dotline').map(renderDotline)}
        {lines.filter(line => line.kind === 'dots').map(line => (
          <g key={`dots-${line.label}`}>
            {line.points.filter(point => Number.isFinite(point.y)).map((point, index) => (
              <path key={index}
                d={`M ${toX(point.x)} ${line.axis === 'if' ? toIfY(point.y) : line.axis === 'right' ? toRightY(point.y) : toLeftY(point.y)} h .01`}
                fill="none" stroke={line.color} strokeWidth={5} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            ))}
          </g>
        ))}

        {tsbPts.length > 0 && TSB_REFERENCE_BANDS.map(zone => {
          if (zone.max <= leftRange.min || zone.max >= leftRange.max) return null;
          const y = toLeftY(zone.max);
          return <line key={zone.key} pointerEvents="none"
            x1={PAD_L} x2={VIEWBOX_WIDTH - PAD_R} y1={y} y2={y}
            stroke={zone.color} strokeOpacity={0.5} strokeDasharray="3 5" />;
        })}

        {focusedX != null && (
          <line
            x1={focusedX}
            y1={PAD_T}
            x2={focusedX}
            y2={PAD_T + CHART_H}
            stroke={CURSOR_COLOR}
            strokeWidth={1.2}
            strokeDasharray="4 3"
            opacity={0.7}
          />
        )}
        </g>
      </svg>

      {/* HTML labels keep their proportions when the SVG stretches to fit the card. */}
      <div dir="ltr" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', fontSize: 10, lineHeight: 1.5, color: LABEL_COLOR }}>
        {bandLabels.map(({ zone, y }) => (
          <span key={zone.key} dir={isRtl ? 'rtl' : 'ltr'}
            aria-label={`${t(`chartReference.tsb.${zone.key}`)} · ${zone.range}`}
            style={{ position: 'absolute', left: `calc(${PAD_L / VIEWBOX_WIDTH * 100}% + 8px)`, top: y,
              transform: 'translateY(-50%)', padding: '1px 5px', borderRadius: 4,
              background: 'var(--surface-0)', color: 'var(--fg-2)', borderInlineStart: `2px solid ${zone.color}`,
              fontSize: 10, lineHeight: '16px', whiteSpace: 'nowrap' }}>
            {t(`chartReference.tsb.short.${zone.key}`)}
          </span>
        ))}
        {leftAxisLabels.map((label, i) => (
          <span key={`left-${i}`} style={{ position: 'absolute',
            right: `calc(${100 - PAD_L / VIEWBOX_WIDTH * 100}% + 4px)`,
            top: `${(PAD_T + CHART_H * i / (leftAxisLabels.length - 1)) / VIEWBOX_HEIGHT * 100}%`,
            transform: 'translateY(-50%)', whiteSpace: 'nowrap' }}>{displayDigits(label)}</span>
        ))}
        {hasIfAxis && <>
          <span style={{ position: 'absolute', left: 'calc(100% + 4px)', top: 0, color: EMBER, fontWeight: 700 }}>IF</span>
          {[0, 1, 2, 3, 4].map(i => <span key={`if-${i}`} style={{ position: 'absolute', left: 'calc(100% + 4px)',
            top: `${(PAD_T + CHART_H * i / 4) / VIEWBOX_HEIGHT * 100}%`, color: EMBER,
            transform: 'translateY(-50%)', whiteSpace: 'nowrap' }}>{displayDigits((ifMax * (1 - i / 4)).toFixed(2))}</span>)}
        </>}
        {rightAxisLabels.map((label, i) => (
          <span key={`right-${i}`} style={{ position: 'absolute',
            left: `calc(${(VIEWBOX_WIDTH - PAD_R) / VIEWBOX_WIDTH * 100}% + 4px)`,
            top: `${(PAD_T + CHART_H * i / (rightAxisLabels.length - 1)) / VIEWBOX_HEIGHT * 100}%`,
            transform: 'translateY(-50%)', whiteSpace: 'nowrap' }}>{displayDigits(label)}</span>
        ))}
        {xAxisTicks.map((tick, i) => (
          <span key={`date-${i}`} dir={isRtl ? 'rtl' : 'ltr'} style={{ position: 'absolute',
            left: `${toX(tick.t) / VIEWBOX_WIDTH * 100}%`,
            top: `${(PAD_T + CHART_H) / VIEWBOX_HEIGHT * 100}%`, marginTop: 6,
            transform: i === 0 ? undefined : i === xAxisTicks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
            whiteSpace: 'nowrap' }}>{displayDigits(tick.label)}</span>
        ))}
      </div>

      {focusedT != null && (
        <div style={TOOLTIP_STYLE}>
          <span style={{ fontSize: 9, color: LABEL_COLOR, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {formatDateLabel(focusedT, i18n.language)}
          </span>
          {legendItems.map((item) => (
            <span key={item.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 9, fontWeight: 600, color: item.color, whiteSpace: 'nowrap' }}>
              <span style={{ width: 5, height: 5, borderRadius: 999, background: item.color, flexShrink: 0 }} />
              {pmcSeriesLabel(item.label, t)}
              {item.value != null ? `: ${displayDigits(Math.round(item.value))}` : ''}
            </span>
          ))}
        </div>
      )}

      {viewState.zoom > 1 && (
        <button
          type="button"
          onClick={() => setViewState({ zoom: 1, pan: 0 })}
          style={ZOOM_RESET_STYLE}
          title="Reset zoom (double-click chart)"
        >
          ⊖ ×{displayDigits(viewState.zoom.toFixed(1))}
        </button>
      )}
      </div>
      {pmcLegendItems.length > 0 && (
        <div className="pmc-interactive-legend" aria-label={t('charts.pmcLegend.controls', { defaultValue: 'Chart series' })}>
          {allLines.some(line => line.label.includes('CTL')) && <button type="button"
            className="pmc-interactive-legend__item" aria-pressed={ctlOnly}
            onClick={() => {
              if (ctlOnly) setHiddenSeries(new Set(previousSeries.current));
              else { previousSeries.current = new Set(hiddenSeries); setHiddenSeries(new Set(['atl', 'tsb', 'tss'])); }
            }}>{t(ctlOnly ? 'charts.ctlOverview' : 'charts.pmcSeries.focus')}</button>}
          {pmcLegendItems.map((item) => {
            const visible = !hiddenSeries.has(item.key);
            return (
              <button
                key={item.key}
                type="button"
                className={`pmc-interactive-legend__item${visible ? '' : ' is-hidden'}`}
                aria-pressed={visible}
                onClick={() => toggleSeries(item.key)}
              >
                <ChartSeriesSwatch color={item.color} dash={item.dash} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {peaksEnabled && <PmcPeakTrendPanel lines={peakLines} toX={toX} visibleRange={visibleRange}
        focusedT={focusedT} ticks={xAxisTicks} fullscreen={fullscreen} onWorkoutClick={onWorkoutClick} />}
      </div>

      <div style={{ fontSize: 9, color: 'var(--fg-4)', marginTop: 4, textAlign: 'right' }}>
        Scroll to zoom · drag to pan · double-click to reset
      </div>
    </div>
  );
}

const TOOLTIP_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px 12px',
  background: 'transparent',
  pointerEvents: 'none',
  zIndex: 10,
  padding: '2px 6px',
};

const ZOOM_RESET_STYLE: React.CSSProperties = {
  position: 'absolute',
  bottom: 28,
  right: 6,
  background: 'rgba(26,31,43,0.72)',
  borderRadius: 10,
  padding: '4px 8px',
  border: 'none',
  cursor: 'pointer',
  fontSize: 10,
  color: '#FFFFFF',
  fontWeight: 600,
  zIndex: 20,
};

export type { PmcChartSettings };
